import { useEffect, useState, useCallback } from 'react'
import { api } from '../lib/api'
import type { ActivityLog } from '@shared/types'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { Badge } from '../components/ui/Badge'

const ENTITY_TYPES = ['', 'client', 'project', 'proposal', 'invoice', 'deal', 'task', 'purchase_order', 'floor_plan', 'user'] as const

export default function ActivityLogPage() {
  const [logs, setLogs] = useState<ActivityLog[]>([])
  const [loading, setLoading] = useState(true)
  const [entityType, setEntityType] = useState('')

  const loadLogs = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ limit: '100' })
      if (entityType) params.set('entity_type', entityType)
      const result = await api.get<ActivityLog[]>(`/activity-log?${params}`)
      setLogs(result)
    } catch { /* handled */ }
    finally { setLoading(false) }
  }, [entityType])

  useEffect(() => { loadLogs() }, [loadLogs])

  function formatAction(action: string) {
    return action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  }

  function formatEntityType(type: string) {
    return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  }

  function timeAgo(dateStr: string) {
    const diff = Date.now() - new Date(dateStr).getTime()
    const minutes = Math.floor(diff / 60000)
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days < 7) return `${days}d ago`
    return new Date(dateStr).toLocaleDateString()
  }

  return (
    <div className="page-enter">
      <div className="page-header">
        <h1 className="page-title" style={{ fontSize: 22 }}>Activity Log</h1>
      </div>

      <div className="mb-4">
        <select
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          className="input max-w-xs"
          style={{ fontSize: 14 }}
        >
          <option value="">All types</option>
          {ENTITY_TYPES.filter(Boolean).map((t) => (
            <option key={t} value={t}>{formatEntityType(t)}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <TableSkeleton />
      ) : logs.length === 0 ? (
        <EmptyState
          title="No activity"
          description="Activity will appear here as actions are performed."
        />
      ) : (
        <div className="space-y-1">
          {logs.map((log) => (
            <div key={log.id} className="card flex items-start gap-3 p-3">
              <div
                className="flex items-center justify-center rounded-full shrink-0 mt-0.5"
                style={{ width: 32, height: 32, background: 'var(--primary-soft)' }}
              >
                <span style={{ color: 'var(--primary)', fontSize: 12, fontWeight: 700 }}>
                  {log.entity_type?.charAt(0).toUpperCase() ?? 'A'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>
                    {formatAction(log.action)}
                  </span>
                  <Badge v="info">{formatEntityType(log.entity_type)}</Badge>
                </div>
                {log.detail && (
                  <p className="line-clamp-2" style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{log.detail}</p>
                )}
                <p className="mono" style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4 }}>{timeAgo(log.performed_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
